// GroupCreate Component - Wizard for creating a new group

import React, { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Users,
  Lock,
  Globe,
  EyeOff,
  Check,
  Image,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { createGroup } from '../../services/groupsService';
import type {
  GroupCategory,
  GroupVisibility,
  GroupJoinPolicy,
  GroupCreateInput,
} from '../../types/groups';

interface GroupCreateProps {
  onBack: () => void;
  onSuccess: (groupId: string) => void;
}

const CATEGORIES: { value: GroupCategory; label: string; description: string; icon: string }[] = [
  { value: 'social', label: 'Gesellig', description: 'Treffen, Stammtische, Networking', icon: '🎉' },
  { value: 'hobby', label: 'Hobby', description: 'Gemeinsame Interessen & Hobbys', icon: '🎨' },
  { value: 'neighborhood', label: 'Nachbarschaft', description: 'Lokale Community', icon: '🏘️' },
  { value: 'food_drink', label: 'Essen & Trinken', description: 'Food Lover & Geniesser', icon: '🍕' },
  { value: 'culture', label: 'Kultur', description: 'Kunst, Musik, Theater', icon: '🎭' },
  { value: 'learning', label: 'Lernen', description: 'Bildung & Austausch', icon: '📚' },
  { value: 'sports', label: 'Sport', description: 'Fitness & Outdoor', icon: '⚽' },
  { value: 'nature', label: 'Natur', description: 'Wandern, Outdoor, Umwelt', icon: '🌲' },
];

const VISIBILITY_OPTIONS: { value: GroupVisibility; label: string; description: string; icon: typeof Globe }[] = [
  {
    value: 'public',
    label: 'Oeffentlich',
    description: 'Jeder kann die Gruppe finden und beitreten',
    icon: Globe,
  },
  {
    value: 'private',
    label: 'Privat',
    description: 'Sichtbar, aber Beitritt nur auf Anfrage',
    icon: Lock,
  },
  {
    value: 'secret',
    label: 'Geheim',
    description: 'Nicht sichtbar, nur auf Einladung',
    icon: EyeOff,
  },
];

const JOIN_POLICIES: { value: GroupJoinPolicy; label: string; description: string }[] = [
  { value: 'open', label: 'Offen', description: 'Jeder kann sofort beitreten' },
  { value: 'request', label: 'Auf Anfrage', description: 'Admins muessen Anfragen genehmigen' },
  { value: 'invite_only', label: 'Nur Einladung', description: 'Nur per Einladung moeglich' },
];

type Step = 'basics' | 'category' | 'privacy' | 'review';

export function GroupCreate({ onBack, onSuccess }: GroupCreateProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('basics');
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<GroupCategory>('social');
  const [visibility, setVisibility] = useState<GroupVisibility>('public');
  const [joinPolicy, setJoinPolicy] = useState<GroupJoinPolicy>('open');
  const [rules, setRules] = useState<string[]>([]);
  const [newRule, setNewRule] = useState('');

  const steps: Step[] = ['basics', 'category', 'privacy', 'review'];
  const currentStepIndex = steps.indexOf(step);

  const canProceed = () => {
    switch (step) {
      case 'basics':
        return name.trim().length >= 3 && description.trim().length >= 10;
      case 'category':
        return true;
      case 'privacy':
        return true;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setStep(steps[nextIndex]);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(steps[prevIndex]);
    } else {
      onBack();
    }
  };

  const addRule = () => {
    if (newRule.trim() && rules.length < 10) {
      setRules([...rules, newRule.trim()]);
      setNewRule('');
    }
  };

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!user) return;

    setSubmitting(true);
    try {
      const input: GroupCreateInput = {
        name: name.trim(),
        description: description.trim(),
        category,
        visibility,
        joinPolicy,
        rules: rules.length > 0 ? rules : undefined,
      };

      const groupId = await createGroup(input, user.uid, user.displayName || 'Anonym');
      onSuccess(groupId);
    } catch (err) {
      console.error('Error creating group:', err);
      alert('Fehler beim Erstellen der Gruppe');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Neue Gruppe</h1>
              <p className="text-sm text-gray-500">
                Schritt {currentStepIndex + 1} von {steps.length}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="flex gap-1 mt-4">
            {steps.map((s, i) => (
              <div
                key={s}
                className={`flex-1 h-1 rounded-full ${
                  i <= currentStepIndex ? 'bg-indigo-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Step: Basics */}
        {step === 'basics' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Grundlagen</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gruppenname *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="z.B. Muenchner Kaffee-Liebhaber"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    maxLength={60}
                  />
                  <p className="text-xs text-gray-500 mt-1">{name.length}/60 Zeichen</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Beschreibung *
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Worum geht es in deiner Gruppe? Was koennen Mitglieder erwarten?"
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-500 mt-1">{description.length}/500 Zeichen</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step: Category */}
        {step === 'category' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Kategorie wählen</h2>

              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => setCategory(cat.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      category === cat.value
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-2xl">{cat.icon}</span>
                    <h3 className="font-medium text-gray-900 mt-2">{cat.label}</h3>
                    <p className="text-sm text-gray-500">{cat.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step: Privacy */}
        {step === 'privacy' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Sichtbarkeit</h2>

              <div className="space-y-3">
                {VISIBILITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setVisibility(opt.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-start gap-4 ${
                      visibility === opt.value
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${
                      visibility === opt.value ? 'bg-indigo-100' : 'bg-gray-100'
                    }`}>
                      <opt.icon className={`w-5 h-5 ${
                        visibility === opt.value ? 'text-indigo-600' : 'text-gray-500'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{opt.label}</h3>
                      <p className="text-sm text-gray-500">{opt.description}</p>
                    </div>
                    {visibility === opt.value && (
                      <Check className="w-5 h-5 text-indigo-500 ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Beitrittspolitik</h2>

              <div className="space-y-3">
                {JOIN_POLICIES.map((policy) => (
                  <button
                    key={policy.value}
                    onClick={() => setJoinPolicy(policy.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      joinPolicy === policy.value
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{policy.label}</h3>
                        <p className="text-sm text-gray-500">{policy.description}</p>
                      </div>
                      {joinPolicy === policy.value && (
                        <Check className="w-5 h-5 text-indigo-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Group Rules */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Gruppenregeln (optional)</h2>
              <p className="text-sm text-gray-500 mb-4">
                Definiere Verhaltensregeln fuer deine Gruppe
              </p>

              {rules.length > 0 && (
                <div className="space-y-2 mb-4">
                  {rules.map((rule, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="w-6 h-6 flex items-center justify-center bg-indigo-100 text-indigo-600 text-sm rounded-full font-medium">
                        {index + 1}
                      </span>
                      <span className="flex-1 text-gray-700">{rule}</span>
                      <button
                        onClick={() => removeRule(index)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {rules.length < 10 && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newRule}
                    onChange={(e) => setNewRule(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addRule()}
                    placeholder="Neue Regel hinzufuegen..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={addRule}
                    disabled={!newRule.trim()}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
                  >
                    Hinzufuegen
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step: Review */}
        {step === 'review' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Preview Header */}
              <div className="h-32 bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                <Users className="w-16 h-16 text-white/30" />
              </div>

              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700">
                    {CATEGORIES.find(c => c.value === category)?.label}
                  </span>
                  <span className="text-xs text-gray-500">
                    {VISIBILITY_OPTIONS.find(v => v.value === visibility)?.label}
                  </span>
                </div>

                <h2 className="text-2xl font-bold text-gray-900">{name}</h2>
                <p className="text-gray-600 mt-2">{description}</p>

                <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    1 Mitglied
                  </span>
                  <span>
                    Beitritt: {JOIN_POLICIES.find(p => p.value === joinPolicy)?.label}
                  </span>
                </div>

                {rules.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <h3 className="font-medium text-gray-900 mb-2">Gruppenregeln</h3>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {rules.map((rule, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-indigo-500">{i + 1}.</span>
                          {rule}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
              <p className="text-sm text-indigo-700">
                <strong>Tipp:</strong> Du kannst nach dem Erstellen ein Coverbild hinzufuegen und Orte mit deiner Gruppe verknuepfen.
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-4 mt-8">
          {step !== 'review' ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-indigo-500 text-white rounded-xl font-medium hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Weiter
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-indigo-500 text-white rounded-xl font-medium hover:bg-indigo-600 disabled:opacity-50 transition-colors"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Erstelle Gruppe...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Gruppe erstellen
                </>
              )}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

export default GroupCreate;
